import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowDown, ArrowUp, ImagePlus, Trash2 } from 'lucide-react';
import { DEFAULT_CATEGORIES } from '../../data/seedProducts';
import { useProducts } from '../../context/ProductContext';
import type { ProductInput } from '../../types';
import {
  formatBenefits,
  formatSpecs,
  MAX_PRODUCT_IMAGES,
  parseBenefits,
  parseSpecs,
  PRODUCT_IMAGE_ACCEPT,
  resolveProductImageUrl,
  slugify,
  validateProductImageFiles,
} from '../../utils/product';

const emptyForm = {
  id: '',
  name: '',
  description: '',
  price: '0',
  currency: 'TRY' as const,
  priceLabel: '',
  category: DEFAULT_CATEGORIES[0],
  benefits: '',
  specs: '',
  stock: '10',
  usePriceLabel: false,
};

type PendingImage = {
  id: string;
  file: File;
  preview: string;
};

export default function AdminProductForm() {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    getProductById,
    createProduct,
    updateProduct,
    uploadProductImages,
    deleteProductImage,
    isLoading,
  } = useProducts();

  const existing = id ? getProductById(id) : undefined;

  const initial = useMemo(() => {
    if (!existing) return emptyForm;
    return {
      id: existing.id,
      name: existing.name,
      description: existing.description,
      price: String(existing.price),
      currency: existing.currency,
      priceLabel: existing.priceLabel ?? '',
      category: existing.category,
      benefits: formatBenefits(existing.benefits),
      specs: formatSpecs(existing.specs),
      stock: String(existing.stock),
      usePriceLabel: Boolean(existing.priceLabel),
    };
  }, [existing]);

  const [form, setForm] = useState(initial);
  const [images, setImages] = useState<string[]>(existing?.images ?? []);
  const [pendingImages, setPendingImages] = useState<PendingImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [imageBusy, setImageBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setForm(initial);
    setImages(existing?.images ?? []);
    setPendingImages([]);
  }, [initial, existing?.images]);

  if (isEdit && isLoading) {
    return (
      <div className="admin-page">
        <p>Yükleniyor...</p>
      </div>
    );
  }

  if (isEdit && !existing) {
    return (
      <div className="admin-page">
        <p>Ürün bulunamadı.</p>
        <Link to="/admin/products" className="back-link">
          <ArrowLeft size={16} aria-hidden />
          Ürünlere Dön
        </Link>
      </div>
    );
  }

  const totalImageCount = images.length + pendingImages.length;

  const updateField = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const buildPayload = (nextImages: string[]): ProductInput => ({
    id: form.id.trim() || slugify(form.name),
    name: form.name,
    description: form.description,
    price: form.usePriceLabel ? 0 : Number(form.price),
    currency: form.currency,
    priceLabel: form.usePriceLabel ? form.priceLabel.trim() || 'Fiyat için iletişime geçin' : undefined,
    category: form.category,
    images: nextImages,
    benefits: parseBenefits(form.benefits),
    specs: parseSpecs(form.specs),
    stock: Number(form.stock),
  });

  const handleSelectFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(event.target.files ?? []);
    event.target.value = '';

    if (selected.length === 0) return;

    setError('');
    const validationError = validateProductImageFiles(selected, totalImageCount);
    if (validationError) {
      setError(validationError);
      return;
    }

    const nextPending = selected.map((file) => ({
      id: crypto.randomUUID(),
      file,
      preview: URL.createObjectURL(file),
    }));

    setPendingImages((prev) => [...prev, ...nextPending]);
  };

  const removePendingImage = (pendingId: string) => {
    setPendingImages((prev) => {
      const target = prev.find((item) => item.id === pendingId);
      if (target) URL.revokeObjectURL(target.preview);
      return prev.filter((item) => item.id !== pendingId);
    });
  };

  const moveImage = (index: number, direction: -1 | 1) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= images.length) return;

    setImages((prev) => {
      const next = [...prev];
      [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
      return next;
    });
  };

  const handleDeleteImage = async (url: string) => {
    if (!isEdit || !id) return;
    if (!confirm('Bu görseli silmek istediğinize emin misiniz?')) return;

    setImageBusy(true);
    setError('');
    try {
      const product = await deleteProductImage(id, url);
      setImages(product.images);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Görsel silinemedi.');
    } finally {
      setImageBusy(false);
    }
  };

  const uploadPendingForProduct = async (productId: string) => {
    if (pendingImages.length === 0) return images;

    const files = pendingImages.map((item) => item.file);
    const product = await uploadProductImages(productId, files);
    pendingImages.forEach((item) => URL.revokeObjectURL(item.preview));
    setPendingImages([]);
    setImages(product.images);
    return product.images;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isEdit && id) {
        await updateProduct(id, buildPayload(images));
        await uploadPendingForProduct(id);
      } else {
        const created = await createProduct(buildPayload([]));
        if (pendingImages.length > 0) {
          await uploadPendingForProduct(created.id);
        }
      }

      navigate('/admin/products');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kayıt başarısız.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <Link to="/admin/products" className="back-link">
            <ArrowLeft size={16} aria-hidden />
            Ürünlere Dön
          </Link>
          <h1>{isEdit ? 'Ürünü Düzenle' : 'Yeni Ürün Ekle'}</h1>
        </div>
      </div>

      {error && <div className="auth-error">{error}</div>}

      <form className="admin-form" onSubmit={handleSubmit}>
        <section className="admin-form-section">
          <h2>Temel Bilgiler</h2>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="name">Ürün Adı *</label>
              <input
                id="name"
                value={form.name}
                onChange={(e) => updateField('name', e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="id">Ürün Kimliği (slug)</label>
              <input
                id="id"
                value={form.id}
                onChange={(e) => updateField('id', e.target.value)}
                placeholder={slugify(form.name) || 'ornek-urun'}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="description">Açıklama *</label>
            <textarea
              id="description"
              rows={4}
              value={form.description}
              onChange={(e) => updateField('description', e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="category">Kategori *</label>
            <input
              id="category"
              list="categories"
              value={form.category}
              onChange={(e) => updateField('category', e.target.value)}
              required
            />
            <datalist id="categories">
              {DEFAULT_CATEGORIES.map((cat) => (
                <option key={cat} value={cat} />
              ))}
            </datalist>
          </div>
        </section>

        <section className="admin-form-section">
          <div className="admin-section-header">
            <h2>Görseller</h2>
            <button
              type="button"
              className="btn btn-outline btn-sm"
              disabled={imageBusy || loading || totalImageCount >= MAX_PRODUCT_IMAGES}
              onClick={() => fileInputRef.current?.click()}
            >
              <ImagePlus size={16} aria-hidden />
              Görsel Seç
            </button>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept={PRODUCT_IMAGE_ACCEPT}
            multiple
            hidden
            onChange={handleSelectFiles}
          />

          <p className="form-hint">
            PNG, JPG veya WEBP — dosya başına en fazla 5 MB. Ürün başına en fazla {MAX_PRODUCT_IMAGES}{' '}
            görsel. İlk görsel vitrin ve sepet önizlemesinde kullanılır.
          </p>

          {totalImageCount === 0 ? (
            <div className="admin-image-empty">
              Henüz görsel yok. Dosya seçerek yükleyebilirsiniz.
            </div>
          ) : (
            <div className="admin-image-grid">
              {images.map((url, index) => (
                <div key={url} className="admin-image-card">
                  <img src={resolveProductImageUrl(url)} alt={`${form.name || 'Ürün'} ${index + 1}`} />
                  <div className="admin-image-card-actions">
                    <span className="admin-image-order">{index === 0 ? 'Kapak' : `#${index + 1}`}</span>
                    <div className="admin-image-card-buttons">
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        disabled={index === 0 || imageBusy || loading}
                        onClick={() => moveImage(index, -1)}
                        aria-label="Görseli yukarı taşı"
                      >
                        <ArrowUp size={14} aria-hidden />
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        disabled={index === images.length - 1 || imageBusy || loading}
                        onClick={() => moveImage(index, 1)}
                        aria-label="Görseli aşağı taşı"
                      >
                        <ArrowDown size={14} aria-hidden />
                      </button>
                      {isEdit && (
                        <button
                          type="button"
                          className="btn btn-outline btn-sm admin-btn-danger"
                          disabled={imageBusy || loading}
                          onClick={() => void handleDeleteImage(url)}
                          aria-label="Görseli sil"
                        >
                          <Trash2 size={14} aria-hidden />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {pendingImages.map((item, index) => (
                <div key={item.id} className="admin-image-card admin-image-card-pending">
                  <img src={item.preview} alt={`Yeni görsel ${index + 1}`} />
                  <div className="admin-image-card-actions">
                    <span className="admin-image-order">Yeni</span>
                    <button
                      type="button"
                      className="btn btn-outline btn-sm admin-btn-danger"
                      disabled={loading}
                      onClick={() => removePendingImage(item.id)}
                      aria-label="Seçimi kaldır"
                    >
                      <Trash2 size={14} aria-hidden />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!isEdit && pendingImages.length > 0 && (
            <p className="form-hint">Yeni görseller ürün kaydedildikten sonra yüklenecek.</p>
          )}
        </section>

        <section className="admin-form-section">
          <h2>Fiyat & Stok</h2>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="currency">Para Birimi</label>
              <select
                id="currency"
                value={form.currency}
                onChange={(e) => updateField('currency', e.target.value as 'EUR' | 'TRY')}
              >
                <option value="EUR">EUR (€)</option>
                <option value="TRY">TRY (₺)</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="price">Fiyat</label>
              <input
                id="price"
                type="number"
                min="0"
                step="0.01"
                value={form.price}
                onChange={(e) => updateField('price', e.target.value)}
                disabled={form.usePriceLabel}
              />
            </div>
          </div>

          <label className="admin-checkbox">
            <input
              type="checkbox"
              checked={form.usePriceLabel}
              onChange={(e) => updateField('usePriceLabel', e.target.checked)}
            />
            Özel fiyat etiketi kullan (ör. &quot;Fiyat için iletişime geçin&quot;)
          </label>

          {form.usePriceLabel && (
            <div className="form-group">
              <label htmlFor="priceLabel">Fiyat Etiketi</label>
              <input
                id="priceLabel"
                value={form.priceLabel}
                onChange={(e) => updateField('priceLabel', e.target.value)}
                placeholder="Fiyat için iletişime geçin"
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="stock">Stok Adedi *</label>
            <input
              id="stock"
              type="number"
              min="0"
              step="1"
              value={form.stock}
              onChange={(e) => updateField('stock', e.target.value)}
              required
            />
            <span className="form-hint">0 girildiğinde ürün stokta yok sayılır.</span>
          </div>
        </section>

        <section className="admin-form-section">
          <h2>Detaylar</h2>

          <div className="form-group">
            <label htmlFor="benefits">Özellikler (virgülle ayırın)</label>
            <input
              id="benefits"
              value={form.benefits}
              onChange={(e) => updateField('benefits', e.target.value)}
              placeholder="Baş ağrısı, Odaklanma, Uyku desteği"
            />
          </div>

          <div className="form-group">
            <label htmlFor="specs">Teknik Özellikler (her satır: Anahtar: Değer)</label>
            <textarea
              id="specs"
              rows={6}
              value={form.specs}
              onChange={(e) => updateField('specs', e.target.value)}
              placeholder={'Marka: DOMAS\nModel: 9128h'}
            />
          </div>
        </section>

        <div className="admin-form-actions">
          <Link to="/admin/products" className="btn btn-outline">İptal</Link>
          <button type="submit" className="btn btn-primary" disabled={loading || imageBusy}>
            {loading ? 'Kaydediliyor...' : isEdit ? 'Güncelle' : 'Ürün Ekle'}
          </button>
        </div>
      </form>
    </div>
  );
}
